/**
 * Application Configuration
 * Centralizes all environment variable access and provides defaults
 */

// API Configuration
export const API_CONFIG = {
  FAKE_DATA_URL: import.meta.env.VITE_FAKE_DATA_API_URL || 'https://api.faketestdata.com/FakeData/random',
  
  // Future TriServe Energy API endpoints
  TRISERVE_API_URL: import.meta.env.VITE_TRISERVE_API_URL || '',
  // TriServe Auth Web API (login/JWT issuance)
  // Set to "relative" or empty string to use same-origin relative URLs (for Docker/nginx proxy)
  TRISERVE_AUTH_API_URL: (() => {
    const envVal = import.meta.env.VITE_TRISERVE_AUTH_API_URL;
    if (envVal === 'relative' || envVal === '') return '';
    return envVal || '';
  })(),
  PAYMENT_API_URL: import.meta.env.VITE_PAYMENT_API_URL || '',
  CREDIT_CHECK_API_URL: import.meta.env.VITE_CREDIT_CHECK_API_URL || '',
  
  // Salesforce Integration
  // Use VITE_SALESFORCE_API_URL env var, or default to localhost:7287 for dev
  // Set to "relative" or empty string to use same-origin relative URLs (for Docker/nginx proxy)
  SALESFORCE_API_URL: (() => {
    const envVal = import.meta.env.VITE_SALESFORCE_API_URL;
    if (envVal === 'relative' || envVal === '') return '';
    return envVal || 'https://localhost:7287';
  })(),
  
  // Service Requests API (Go microservice)
  SERVICE_REQUESTS_API_URL: import.meta.env.VITE_SERVICE_REQUESTS_API_URL || '',

  // Auth API request details
  AUTH_APP_KEY: import.meta.env.VITE_AUTH_APP_KEY || 'triserve-energy',
  AUTH_LOGIN_ENDPOINT:        import.meta.env.VITE_AUTH_LOGIN_ENDPOINT         || '/api/v1/auth/login',
  AUTH_LOGOUT_ENDPOINT:       import.meta.env.VITE_AUTH_LOGOUT_ENDPOINT        || '/api/v1/auth/logout',
  AUTH_SELF_REGISTER_ENDPOINT: import.meta.env.VITE_AUTH_SELF_REGISTER_ENDPOINT || '/api/v1/auth/self-register',
  AUTH_REFRESH_ENDPOINT:      import.meta.env.VITE_AUTH_REFRESH_ENDPOINT       || '/api/v1/auth/refresh-token',
  AUTH_VERIFY_ENDPOINT:       import.meta.env.VITE_AUTH_VERIFY_ENDPOINT        || '/api/v1/auth/verify',

  // SAP Business Partner Integration
  SAP_BP_API_URL: import.meta.env.VITE_SAP_BP_API_URL || 'https://sapdemo21.tricentis.com:44301/sap/opu/odata/SAP/API_BUSINESS_PARTNER/A_BusinessPartner',
  SAP_CSRF_TOKEN: import.meta.env.VITE_SAP_CSRF_TOKEN || '',
};

// Application Configuration
export const APP_CONFIG = {
  NAME: import.meta.env.VITE_APP_NAME || 'TriServe Energy',
  VERSION: import.meta.env.VITE_APP_VERSION || '2.0.0',
  BASE_PATH: import.meta.env.VITE_APP_BASE_PATH || '/TriServeEnergy',
  DEV_MODE: import.meta.env.VITE_DEV_MODE === 'true',
  SHOW_DEBUG_INFO: import.meta.env.VITE_SHOW_DEBUG_INFO === 'true',
  ENABLE_TEST_DATA: import.meta.env.VITE_ENABLE_TEST_DATA === 'true',
};

// Authentication Configuration
export const AUTH_CONFIG = {
  AUTH_BYPASS: import.meta.env.VITE_AUTH_BYPASS === 'true' && import.meta.env.DEV,
  ENABLE_REGISTRATION: import.meta.env.VITE_ENABLE_AUTH_REGISTRATION === 'true' || import.meta.env.DEV,
};

// Development/Debug Configuration
export const DEBUG_CONFIG = {
  LOG_API_CALLS: APP_CONFIG.DEV_MODE,
  SHOW_TEST_BUTTONS: APP_CONFIG.DEV_MODE || APP_CONFIG.ENABLE_TEST_DATA,
  ENABLE_FAKE_API: APP_CONFIG.DEV_MODE,
};

// API Request Configuration
export const REQUEST_CONFIG = {
  TIMEOUT: 10000, // 10 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
};

// Validation function to check required environment variables
export const validateConfig = () => {
  const errors = [];
  
  // Check required variables for production
  if (!APP_CONFIG.DEV_MODE) {
    if (!API_CONFIG.FAKE_DATA_URL) {
      errors.push('VITE_FAKE_DATA_API_URL is required');
    }

    if (import.meta.env.VITE_AUTH_BYPASS === 'true') {
      errors.push('VITE_AUTH_BYPASS is only allowed in development builds');
    }
  }
  
  if (errors.length > 0) {
    console.error('Configuration validation failed:', errors);
    throw new Error(`Configuration validation failed: ${errors.join(', ')}`);
  }
  
  return true;
};

// Log configuration in development mode
if (APP_CONFIG.DEV_MODE && APP_CONFIG.SHOW_DEBUG_INFO) {
  console.log('App Configuration:', {
    ...APP_CONFIG,
    Auth: AUTH_CONFIG,
    APIs: API_CONFIG,
    Debug: DEBUG_CONFIG
  });
}

export default {
  API_CONFIG,
  APP_CONFIG,
  AUTH_CONFIG,
  DEBUG_CONFIG,
  REQUEST_CONFIG,
  validateConfig
};
